import React, { useState, useEffect, useRef } from 'react';
import { Box, Paper, TextField, Button, Typography, Alert, CircularProgress, Link, FormControl, Tabs, Tab, Divider, IconButton, InputAdornment } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { login, register, loginWithGoogle, clearStaleSession } from '../slices/authSlice';
import { useLanguage } from '../contexts/LanguageContext';
import { Tooltip } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PaidIcon from '@mui/icons-material/Paid';
import axios from 'axios';
import apiClient, { API_ROOT_URL } from '../services/api';
import goldColors from '../themes/goldColors';

// Pausa simple usada entre reintentos de login.
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// "Continuar con Google" (6 ago 2026, pedido de Augusto). Mismo patrón que
// REACT_APP_TURNSTILE_SITE_KEY en RegisterFormSinglePage.js: mientras esta
// variable de entorno de Netlify quede vacía, el botón simplemente no se
// muestra -- el backend tampoco lo exige (GOOGLE_CLIENT_ID vacío en
// Render, ver core/config.py). Ambos lados se activan juntos el día que
// Augusto cree las credenciales en Google Cloud Console.
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const GOOGLE_GSI_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

// Login validation schema that only accepts email
const loginValidationSchema = Yup.object({
  loginId: Yup.string()
    .required('Email is required')
    .email('Please enter a valid email address'),
  password: Yup.string()
    .min(1, 'Password is required') // Change to min 1 to be more permissive
    .required('Password is required'),
});

// Registration validation schema
const registerValidationSchema = Yup.object({
  email: Yup.string()
    .required('Email is required')
    .email('Please enter a valid email address'),
  firstName: Yup.string()
    .required('First name is required')
    .min(2, 'First name must be at least 2 characters'),
  lastName: Yup.string()
    .required('Last name is required')
    .min(2, 'Last name must be at least 2 characters'),
  user_id: Yup.string()
    .required('Username is required')
    .min(4, 'Username must be at least 4 characters'),
  password: Yup.string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters'),
  confirmPassword: Yup.string()
    .required('Confirm password is required')
    .oneOf([Yup.ref('password')], 'Passwords must match'),
});

const LoginForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { language, changeLanguage } = useLanguage();
  
  // State to track whether to show login or register form
  const [formMode, setFormMode] = useState('login');

  // Ver/ocultar contraseña en el login (el registro ya tenía este toggle).
  const [showPassword, setShowPassword] = useState(false);

  // Mensaje de estado durante el submit: '' | 'slow' | 'retrying'. El
  // backend está en el plan gratuito de Render, que "duerme" tras 15 min
  // de inactividad y tarda 30-50s+ en despertar en la primera petición.
  // Sin esto, el usuario solo veía un spinner sin contexto y, si el login
  // hacía timeout justo cuando el servidor recién arrancaba, un error
  // genérico de "usuario o contraseña incorrecta" — que no era el problema
  // real.
  const [loginStatus, setLoginStatus] = useState('');

  // 3 ago 2026: se quitó el aviso azul "¡Bienvenido al Portal de
  // Clientes!" que aparecía acá cuando se llegaba con ?from=portal (desde
  // el botón "Ingreso" del header). Con el nuevo encabezado de la tarjeta
  // ("Bienvenido de vuelta"), quedaban dos mensajes de bienvenida apilados
  // uno encima del otro -- se veía redundante, como si el rediseño no se
  // hubiera aplicado. El query param ?from=portal ya no se usa para nada,
  // pero se deja el link del header tal cual (no hace daño).

  // Un token viejo (expiró a los 8 días, o quedó invalidado porque el
  // usuario cambió su contraseña — ver token_version en el backend) podía
  // quedarse guardado en localStorage/Redux mucho después de dejar de
  // servir. Esta pantalla siempre mostraba el formulario de login sin
  // fijarse en eso, así que el cliente veía dos cosas contradictorias a la
  // vez: el encabezado (PublicHeader) mostrando "SALIR" como si siguiera
  // logueado (porque lee ese mismo token viejo de Redux), y este mismo
  // formulario pidiéndole iniciar sesión de nuevo.
  //
  // Se verifica el token contra /auth/me apenas se monta la pantalla: si
  // todavía es válido, no tiene sentido mostrarle el login — se le manda
  // directo al tablero. Si ya no es válido, se limpia de una vez para que
  // el encabezado dejé de mostrar "SALIR" y quede consistente con lo que
  // esta pantalla ya sabe.
  useEffect(() => {
    const existingToken = sessionStorage.getItem('token');
    if (!existingToken) return;

    let cancelled = false;
    apiClient
      .get('/auth/me')
      .then(() => {
        if (!cancelled) navigate('/dashboard', { replace: true });
      })
      .catch(() => {
        if (cancelled) return;
        sessionStorage.removeItem('token');
        localStorage.removeItem('user');
        dispatch(clearStaleSession());
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, navigate]);

  // "Despertar" el backend apenas se carga la pantalla de login, en
  // paralelo a que el cliente escribe su correo/contraseña — así, para
  // cuando aprieta "Iniciar Sesión", el servidor gratuito de Render ya
  // tuvo varios segundos de ventaja para salir del modo dormido. Pega a
  // /health (no toca la base de datos, solo confirma que el proceso está
  // vivo) y se ignora cualquier error: es solo un empujón, no una
  // petición que el usuario esté esperando.
  useEffect(() => {
    axios.get(`${API_ROOT_URL}/health`, { timeout: 60000 }).catch(() => {});
  }, []);

  // --- "Continuar con Google" (6 ago 2026) ---
  const googleButtonRef = useRef(null);
  const [googleError, setGoogleError] = useState('');
  // '', 'loading' o 'slow' -- ver nota en handleGoogleCredential.
  const [googleStatus, setGoogleStatus] = useState('');

  // Callback que Google Identity Services invoca con el credential (id_token)
  // apenas el cliente elige su cuenta de Gmail. Reusa el mismo flujo de
  // "éxito" que el login normal (guardar token, banderas de sesión, ir a
  // /welcome) -- ver loginFormik.onSubmit más abajo.
  const handleGoogleCredential = async (response) => {
    setGoogleError('');
    setGoogleStatus('loading');

    // 7 ago 2026 (Augusto: "no me permite ingresar con Google... no pasa
    // nada"): reproduciéndolo, el login SÍ funcionaba -- lo que pasaba es
    // que el backend en Render (plan gratis) estaba dormido y tardó ~15s
    // en despertar, y este flujo, a diferencia del login con contraseña
    // (ver slowTimer en loginFormik.onSubmit más abajo), no mostraba
    // ningún aviso mientras tanto: el botón de Google se queda estático
    // sin importar cuánto tarde, así que parecía "no responder". Mismo
    // aviso de "servidor despertando" a los 4s que ya usa el login normal.
    const slowTimer = setTimeout(() => setGoogleStatus('slow'), 4000);

    try {
      const userData = await dispatch(loginWithGoogle(response.credential)).unwrap();
      const token = userData.access_token || userData.token;
      if (token) {
        sessionStorage.setItem('token', token);
        sessionStorage.setItem('isAuthenticated', 'true');
      }
      sessionStorage.setItem('profileSetupCompleted', 'true');
      localStorage.setItem('profileSetupCompleted', 'true');
      sessionStorage.setItem('onboardingCompleted', 'false');
      localStorage.setItem('onboardingCompleted', 'false');
      window.location.href = '/welcome';
    } catch (error) {
      console.error('Google login failed:', error);
      setGoogleError(
        language === 'es'
          ? 'No se pudo iniciar sesión con Google. Intenta de nuevo.'
          : 'Google sign-in failed. Please try again.'
      );
      setGoogleStatus('');
    } finally {
      clearTimeout(slowTimer);
    }
  };

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return undefined;

    const renderButton = () => {
      if (!window.google?.accounts?.id || !googleButtonRef.current) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCredential,
      });
      googleButtonRef.current.innerHTML = '';
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: 'outline',
        size: 'large',
        width: 328,
        text: 'continue_with',
        locale: language === 'es' ? 'es' : 'en',
      });
    };

    if (window.google?.accounts?.id) {
      renderButton();
      return undefined;
    }

    const existingScript = document.querySelector(`script[src="${GOOGLE_GSI_SCRIPT_SRC}"]`);
    if (existingScript) {
      existingScript.addEventListener('load', renderButton);
      return () => existingScript.removeEventListener('load', renderButton);
    }

    const script = document.createElement('script');
    script.src = GOOGLE_GSI_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = renderButton;
    document.body.appendChild(script);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  // Add a debug logger
  const logFormikState = (formik) => {
    console.log('Form state:', {
      values: formik.values,
      errors: formik.errors,
      isValid: formik.isValid,
      dirty: formik.dirty,
      touched: formik.touched,
      submitting: formik.isSubmitting,
    });
  };

  // Handle tab change between login and register
  const handleTabChange = (event, newValue) => {
    setFormMode(newValue);
  };

  // Login form handling
  const loginFormik = useFormik({
    initialValues: {
      loginId: '',
      password: '',
    },
    validationSchema: loginValidationSchema,
    onSubmit: async (values) => {
      loginFormik.setSubmitting(true);
      setLoginStatus('');

      // Si el submit tarda más de 4s, probablemente el servidor gratuito
      // estaba dormido y recién está despertando — se lo decimos al
      // usuario en vez de dejarlo con un spinner mudo.
      const slowTimer = setTimeout(() => setLoginStatus('slow'), 4000);

      const maxAttempts = 3;
      let lastError = null;

      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        try {
          console.log(`Submitting login form (intento ${attempt}/${maxAttempts})`);
          const userData = await dispatch(login(values)).unwrap();

          clearTimeout(slowTimer);
          setLoginStatus('');

          // El token solo va a sessionStorage (3 ago 2026: ver
          // services/api.js -- así cerrar el navegador cierra la sesión).
          // authSlice.js (login.fulfilled) ya lo guarda ahí también; esto
          // es redundante pero inofensivo.
          if (userData.access_token || userData.token) {
            const token = userData.access_token || userData.token;
            sessionStorage.setItem('token', token);
            sessionStorage.setItem('isAuthenticated', 'true');
          }

          sessionStorage.setItem('profileSetupCompleted', 'true');
          localStorage.setItem('profileSetupCompleted', 'true');
          sessionStorage.setItem('onboardingCompleted', 'false');
          localStorage.setItem('onboardingCompleted', 'false');

          // Usar window.location para forzar redirección completa en lugar de React Router
          window.location.href = '/welcome';
          return; // Importante para evitar que continúe la ejecución
        } catch (error) {
          console.error(`Login failed (intento ${attempt}/${maxAttempts}):`, error);
          lastError = error;

          // Solo reintentamos automáticamente si fue un problema de
          // red/timeout (servidor despertando) — un 401 de credenciales
          // incorrectas no se arregla reintentando.
          const canRetry = error?.isNetworkOrTimeout && attempt < maxAttempts;
          if (canRetry) {
            setLoginStatus('retrying');
            await wait(2500);
            continue;
          }
          break;
        }
      }

      clearTimeout(slowTimer);
      setLoginStatus('');

      const error = lastError || {};
      if (error.isNetworkOrTimeout) {
        loginFormik.setFieldError(
          'general',
          language === 'es'
            ? 'El servidor tardó demasiado en responder (puede haber estado inactivo tras un rato sin uso). Intenta de nuevo en unos segundos.'
            : 'The server took too long to respond (it may have been asleep after a period of inactivity). Please try again in a few seconds.'
        );
      } else if (error.detail) {
        loginFormik.setFieldError('general', error.detail);
      } else if (error.message) {
        loginFormik.setFieldError('general', error.message);
      } else {
        loginFormik.setFieldError(
          'general',
          language === 'es' ? 'No se pudo iniciar sesión. Intenta de nuevo.' : 'Login failed. Please try again.'
        );
      }

      loginFormik.setSubmitting(false);
    },
  });

  // Registration form handling
  const registerFormik = useFormik({
    initialValues: {
      email: '',
      firstName: '',
      lastName: '',
      user_id: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema: registerValidationSchema,
    onSubmit: async (values) => {
      try {
        // Show loading state
        registerFormik.setSubmitting(true);

        console.log('Submitting registration form with values:', values);
        // Try to register
        const userData = await dispatch(register({
          email: values.email,
          password: values.password,
          firstName: values.firstName,
          lastName: values.lastName,
          user_id: values.user_id,
        })).unwrap();
        
        console.log('Registration successful, checking where to navigate');
        
        // IMPORTANTE: Guardar estado de autenticación SOLO en sessionStorage si hay token
        if (userData.access_token || userData.token) {
          const token = userData.access_token || userData.token;
          sessionStorage.setItem('token', token);
          sessionStorage.setItem('isAuthenticated', 'true');
          console.log('Token guardado en sessionStorage (solo para esta sesión):', token);
        }
        
        // Navigate to profile setup
        setTimeout(() => {
          navigate('/profile-setup', { state: { firstName: values.firstName } });
        }, 100);
      } catch (error) {
        console.error('Registration failed:', error);

        // Set appropriate error message based on error type
        if (error.response?.data?.message) {
          registerFormik.setFieldError('general', error.response.data.message);
        } else if (error.response?.data?.detail) {
          registerFormik.setFieldError('general', error.response.data.detail);
        } else if (error.response?.data?.error?.includes('Email already registered')) {
          registerFormik.setFieldError('general', 'This email is already registered. Please log in instead.');
        } else if (error.response?.data?.error?.includes('User ID already taken')) {
          registerFormik.setFieldError('general', 'This username is already taken. Please try another one.');
        } else if (error.response?.status === 500) {
          registerFormik.setFieldError('general', 'Server error. Please try again later.');
        } else {
          registerFormik.setFieldError('general', 'Registration failed. Please try again.');
        }
      } finally {
        // Reset loading state
        registerFormik.setSubmitting(false);
      }
    },
  });

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        // 3 ago 2026: era minHeight 100vh, pero esta caja ya arranca 64px
        // más abajo (el "pt: 8" del Layout público que compensa el header
        // fijo), así que centrar en 100vh completo empujaba la tarjeta de
        // más y dejaba un espacio vacío enorme arriba. Restando esos 64px
        // el centrado queda relativo al área realmente visible.
        minHeight: 'calc(100vh - 64px)',
        py: 4,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: '100%',
          maxWidth: '500px',
          position: 'relative',
          // 3 ago 2026: degradé dorado sutil arriba, detrás del ícono --
          // estaba en el mockup que le mostré a Augusto pero se me quedó
          // fuera al implementar la tarjeta la primera vez.
          backgroundImage: `linear-gradient(180deg, ${alpha(goldColors.main, 0.08)}, transparent 140px)`,
        }}
      >
        {/* 3 ago 2026 (pedido de Augusto): ícono de marca + mensaje de
            bienvenida -- antes la tarjeta era solo bordes y el título
            "Iniciar Sesión" sin ningún elemento visual, se sentía como una
            plantilla default sin personalizar. */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: goldColors.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PaidIcon sx={{ color: 'white', fontSize: 26 }} />
          </Box>
        </Box>
        {/* El selector de idioma ha sido eliminado */}

        {/* Login Header */}
        <Typography
          variant="h5"
          align="center"
          sx={{ mb: 0.5, fontWeight: 'bold' }}
        >
          {language === 'es' ? 'Bienvenido de vuelta' : 'Welcome back'}
        </Typography>
        <Typography
          variant="body2"
          align="center"
          color="text.secondary"
          sx={{ mb: 3 }}
        >
          {language === 'es'
            ? 'Ingresa a tu cuenta de PCM Finanzas'
            : 'Log in to your PCM Finanzas account'}
        </Typography>

        {/* LOGIN FORM */}
          <>
            {loginFormik.errors.general && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {loginFormik.errors.general}
                {loginFormik.errors.general.includes('Account not found') && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {language === 'es' ? '¿No tienes una cuenta? ' : 'Don\'t have an account? '}
                    <Link 
                      component="button" 
                      variant="body2"
                      onClick={() => setFormMode('register')}
                    >
                      {language === 'es' ? 'Regístrate aquí' : 'Register here'}
                    </Link>
                  </Typography>
                )}
              </Alert>
            )}
            <form onSubmit={loginFormik.handleSubmit}>
              <TextField
                fullWidth
                id="loginId"
                name="loginId"
                label={language === 'es' ? 'Correo electrónico' : 'Email Address'}
                placeholder="email@example.com"
                value={loginFormik.values.loginId}
                onChange={(e) => {
                  loginFormik.handleChange(e);
                  // Clear any previous error when loginId changes
                  loginFormik.setFieldError('loginId', '');
                }}
                error={loginFormik.touched.loginId && Boolean(loginFormik.errors.loginId)}
                helperText={(loginFormik.touched.loginId && loginFormik.errors.loginId) || (language === 'es' ? "Introduce tu correo electrónico completo" : "Enter your complete email address")}
                margin="normal"
                autoComplete="username"
                // El navegador autocompleta este campo (correo/usuario
                // guardado) escribiendo directo en el <input> del DOM. Eso
                // puede llegar antes de que Formik/MUI se enteren de que el
                // valor ya no está vacío, así que el label flotante se
                // queda "adentro" del campo en vez de subir arriba — y se
                // ve superpuesto con el texto autocompletado (el efecto
                // "letras encimadas" que se reportó). Forzar shrink:true
                // deja el label siempre arriba, sin depender de ese estado,
                // y el override de -webkit-autofill evita el tinte amarillo
                // de Chrome que agravaba el efecto visual.
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& input:-webkit-autofill': {
                    WebkitBoxShadow: '0 0 0 1000px #fff inset',
                    WebkitTextFillColor: '#000',
                  },
                }}
              />
              <TextField
                fullWidth
                id="password"
                name="password"
                label={language === 'es' ? 'Contraseña' : 'Password'}
                type={showPassword ? 'text' : 'password'}
                value={loginFormik.values.password}
                onChange={loginFormik.handleChange}
                error={loginFormik.touched.password && Boolean(loginFormik.errors.password)}
                helperText={loginFormik.touched.password && loginFormik.errors.password}
                margin="normal"
                autoComplete="current-password"
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& input:-webkit-autofill': {
                    WebkitBoxShadow: '0 0 0 1000px #fff inset',
                    WebkitTextFillColor: '#000',
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={language === 'es' ? 'mostrar/ocultar contraseña' : 'toggle password visibility'}
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                        tabIndex={-1}
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              <Box sx={{ textAlign: 'right', mt: 1 }}>
                <Link href="/forgot-password" variant="body2">
                  {language === 'es' ? '¿Olvidaste tu contraseña?' : 'Forgot password?'}
                </Link>
              </Box>
              <Button
                fullWidth
                variant="contained"
                type="submit"
                disabled={loginFormik.isSubmitting || (!loginFormik.values.loginId || !loginFormik.values.password)}
                sx={{
                  mt: 3,
                  // 3 ago 2026: botón en el dorado de marca (goldColors, el
                  // mismo del ícono del header) en vez del azul default de
                  // MUI, para que la pantalla se sienta parte de PCM.
                  bgcolor: goldColors.main,
                  '&:hover': { bgcolor: goldColors.dark },
                }}
              >
                {loginFormik.isSubmitting ? (
                  <>
                    <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                    {loginStatus === 'retrying'
                      ? (language === 'es' ? 'Reintentando...' : 'Retrying...')
                      : loginStatus === 'slow'
                      ? (language === 'es' ? 'El servidor está iniciando, un momento...' : 'Server is starting up, one moment...')
                      : (language === 'es' ? 'Iniciando sesión...' : 'Logging in...')}
                  </>
                ) : (
                  language === 'es' ? 'Iniciar Sesión' : 'Login'
                )}
              </Button>

              {/* "Continuar con Google" (6 ago 2026) -- solo se muestra si
                  hay client ID configurado (Netlify env var). Ver nota en
                  GOOGLE_CLIENT_ID más arriba. */}
              {GOOGLE_CLIENT_ID && (
                <>
                  <Divider sx={{ my: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      {language === 'es' ? 'o' : 'or'}
                    </Typography>
                  </Divider>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Box ref={googleButtonRef} />
                  </Box>
                  {/* Feedback mientras procesa (7 ago 2026, ver nota en
                      handleGoogleCredential) -- antes no había nada acá y
                      el botón se quedaba igual sin importar cuánto tardara
                      el backend en responder. */}
                  {googleStatus === 'loading' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 1 }}>
                      <CircularProgress size={14} />
                      <Typography variant="caption" color="text.secondary">
                        {language === 'es' ? 'Conectando con Google...' : 'Connecting to Google...'}
                      </Typography>
                    </Box>
                  )}
                  {googleStatus === 'slow' && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                      {language === 'es'
                        ? 'El servidor estaba inactivo y está despertando, un momento...'
                        : 'The server was asleep and is waking up, one moment...'}
                    </Typography>
                  )}
                  {googleError && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                      {googleError}
                    </Typography>
                  )}
                </>
              )}
            </form>
          </>
      </Paper>
    </Box>
  );
};

export default LoginForm;
